/// <amd-module name="Wit/Common/WorkItemDialogs/Components/CopyWorkItemDialog" />

import * as React from "react";
import { ITeamProject, ITfsProjectService } from "TFS/Services/TfsProjectService";
import { IReadyableReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { traceException } from "VSS/Platform/Trace";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemTypesService, WorkItemCategoryConstants } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItemFormDialog } from "./WorkItemFormDialog";

export interface ICopyWorkItemDialogProps {
    selectedItem: INamedFieldDataDictionary;
    onDismiss: () => void;
}

interface ICopyWorkItemDialogState {
    error?: string;
    loadingTypes: boolean;
    working: boolean;
    workItemTypes: IListBoxItem[] | undefined;
}

export class CopyWorkItemDialog extends VssComponent<ICopyWorkItemDialogProps, ICopyWorkItemDialogState> {
    private _projectSelection = new DropdownSelection();
    private _workItemTypeSelection = new DropdownSelection();
    private _includeLinks = new ObservableValue(false);
    private _includeAttachments = new ObservableValue(false);
    private _includeChildren = new ObservableValue(false);
    private _projectWorkItemTypes: { [projectId: string]: string[] } = {};
    private _projects: IReadyableReadonlyObservableArray<ITeamProject>;
    private _lastSelectedWorkItemType: string | undefined;
    private _isIncludeChildEnabled = new ObservableValue(true);
    private copyWorkItemExcludedCategories = [
        WorkItemCategoryConstants.hidden,
        WorkItemCategoryConstants.testPlan,
        WorkItemCategoryConstants.testSuite,
        WorkItemCategoryConstants.testSharedStep,
        WorkItemCategoryConstants.testSharedParameter
    ];

    public constructor(props: ICopyWorkItemDialogProps, context: IVssComponentContext) {
        super(props, context);

        this._initialize();

        this.state = {
            loadingTypes: true,
            working: false,
            workItemTypes: undefined
        };
    }

    private async _initialize() {
        const projectService = this.context.pageContext.getService<ITfsProjectService>("ITfsProjectService");
        this._projects = projectService.getAllProjects();
        await projectService.getAllProjectsLoadedPromise();
        const projectIdx = this._projects.value.findIndex(p => p.name === this.props.selectedItem[CoreFieldRefNames.TeamProject]);
        this._projectSelection.select(projectIdx >= 0 ? projectIdx : 0);
        this._getWorkItemTypes(this._projects.value[projectIdx].id);
    }

    public render() {
        return (
            <Dialog
                className="copy-work-item-dialog"
                escDismiss
                footerButtonProps={[
                    {
                        disabled: this.state.working,
                        text: Resources.Cancel,
                        onClick: this.props.onDismiss
                    },
                    {
                        disabled: this.state.loadingTypes || this.state.working,
                        primary: true,
                        text: Resources.Copy,
                        onClick: this._copyWorkItem
                    }
                ]}
                lightDismiss={false}
                overlay={this.state.working ? { spinnerAriaLabel: Resources.Copying } : undefined}
                resizable={true}
                showCloseButton={true}
                titleProps={{ text: Resources.CopyWorkItemDialogTitle }}
                onDismiss={this.props.onDismiss}
            >
                <Observer ready={this._projects.ready} isIncludeChildEnabled={this._isIncludeChildEnabled}>
                    {({ ready, isIncludeChildEnabled }: { ready: boolean; isIncludeChildEnabled: boolean }) => {
                        if (!ready) {
                            return <Spinner />;
                        }

                        return (
                            <div className="rhythm-vertical-16">
                                {this.state.error && <MessageCard severity={MessageCardSeverity.Error}>{this.state.error}</MessageCard>}
                                <div>
                                    {format(
                                        Resources.CopyWorkItemDialogText,
                                        this.props.selectedItem[CoreFieldRefNames.WorkItemType],
                                        this.props.selectedItem[CoreFieldRefNames.Id],
                                        this.props.selectedItem[CoreFieldRefNames.Title]
                                    )}
                                </div>
                                <FormItem label={Resources.Project}>
                                    <Dropdown
                                        ariaLabel={Resources.Project}
                                        disabled={this.state.loadingTypes}
                                        items={this._projects.value.map(p => ({ text: p.name, id: p.id }))}
                                        selection={this._projectSelection}
                                        onSelect={(evt, item) => {
                                            this._getWorkItemTypes(item.id).then(_ => this.updateIncludeChildCheckboxState());
                                        }}
                                    />
                                </FormItem>
                                <FormItem label={Resources.WorkItemType}>
                                    <Dropdown
                                        ariaLabel={Resources.WorkItemType}
                                        disabled={this.state.loadingTypes}
                                        items={this.state.workItemTypes || []}
                                        selection={this._workItemTypeSelection}
                                        onSelect={(evt, item) => {
                                            this._lastSelectedWorkItemType = item.id;
                                            this.updateIncludeChildCheckboxState();
                                        }}
                                    />
                                </FormItem>
                                {/* wrap all the Checkboxes in a div so they don't get the vertical separation of the other elements */}
                                {/* wrap each Checkbox in a div so they stack vertically, subverting their inline-flex property */}
                                <div>
                                    <div>
                                        <Checkbox
                                            checked={this._includeLinks}
                                            label={Resources.IncludeLinks}
                                            onChange={(evt, checked) => (this._includeLinks.value = checked)}
                                        />
                                    </div>
                                    <div>
                                        <Checkbox
                                            checked={this._includeAttachments}
                                            label={Resources.IncludeAttachments}
                                            onChange={(evt, checked) => (this._includeAttachments.value = checked)}
                                        />
                                    </div>
                                    <div>
                                        <Checkbox
                                            disabled={!isIncludeChildEnabled}
                                            checked={this._includeChildren}
                                            label={Resources.IncludeChildren}
                                            onChange={(evt, checked) => (this._includeChildren.value = checked)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </Observer>
            </Dialog>
        );
    }

    /**
     * Fetch the work item types for a project and set them to be displayed
     * @param projectId
     */
    private async _getWorkItemTypes(projectId: string) {
        if (!this._projectWorkItemTypes[projectId]) {
            if (!this.state.loadingTypes) {
                this.setState({ loadingTypes: true });
            }
            const workItemTypeService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
            const wits = await workItemTypeService.getWorkItemTypes(projectId, this.copyWorkItemExcludedCategories);
            this._projectWorkItemTypes[projectId] = wits;
        }
        this._setWorkItemTypes();
    }

    /**
     * Display the work item types for the currently selected project.
     */
    private _setWorkItemTypes() {
        const projIndex = getSelectedIndex(this._projectSelection);
        if (projIndex !== undefined) {
            const projId = this._projects.value[projIndex].id;
            const workItemTypes = this._projectWorkItemTypes[projId];
            if (workItemTypes) {
                this.setState({ loadingTypes: false, workItemTypes: workItemTypes.map(wit => ({ id: wit, text: wit })) });
                const selectedWit = this._lastSelectedWorkItemType || this.props.selectedItem[CoreFieldRefNames.WorkItemType];
                const witIndex = workItemTypes.indexOf(selectedWit);
                this._workItemTypeSelection.select(witIndex >= 0 ? witIndex : 0);
            }
        }
    }

    private _copyWorkItem = async () => {
        const workItemId = this.props.selectedItem[CoreFieldRefNames.Id] as number;
        const includeLinks = this._includeLinks.value;
        const includeAttachments = this._includeAttachments.value;
        const includeChildren = this._includeChildren.value;

        this.setState({ working: true });

        try {
            const modelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const workItemTypeService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
            const [source] = await modelService.getWorkItems([workItemId]);
            const projectIndex = getSelectedIndex(this._projectSelection);
            const projectId = projectIndex !== undefined && this._projects.value[projectIndex].id;
            const typeIndex = getSelectedIndex(this._workItemTypeSelection);
            const typeName = typeIndex !== undefined && this.state.workItemTypes && this.state.workItemTypes[typeIndex].id;
            const typeData = typeName && projectId && (await workItemTypeService.getWorkItemTypeData(projectId, [typeName]));

            if (projectId && typeData) {
                let copy: IWorkItem;
                if (includeChildren) {
                    copy = await source.copyWithChildren(this.context.pageContext, projectId, typeData[0], includeLinks, includeAttachments);
                } else {
                    copy = await source.copy(this.context.pageContext, projectId, typeData[0], includeLinks, includeAttachments);
                }
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => <WorkItemFormDialog workItem={copy} onDismiss={onDismiss} />);
                this.props.onDismiss();
            }
        } catch (error) {
            console.error("%O", error);
            this.setState({ error: error.message || String(error), working: false });
            traceException(this.context.pageContext, "WIT", "CopyWorkItem", error, { workItemId, includeChildren, includeLinks, includeAttachments });
        }
    };

    private updateIncludeChildCheckboxState(): void {
        const index = getSelectedIndex(this._projectSelection);
        const areWitAndProjectSame =
            index !== undefined &&
            this.props.selectedItem[CoreFieldRefNames.TeamProject] === this._projects.value[index].name &&
            (this._lastSelectedWorkItemType === undefined ||
                this.props.selectedItem[CoreFieldRefNames.WorkItemType] === this._lastSelectedWorkItemType);

        if (areWitAndProjectSame !== this._isIncludeChildEnabled.value) {
            this._isIncludeChildEnabled.value = areWitAndProjectSame;

            if (this._includeChildren.value && !this._isIncludeChildEnabled.value) {
                this._includeChildren.value = false;
            }
        }
    }
}

function getSelectedIndex(selection: DropdownSelection) {
    return selection.value && selection.value.length > 0 ? selection.value[0].beginIndex : undefined;
}
